import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUpRight } from 'lucide-react';
import { Overline, Reveal } from '../components/Reveal';
import { CTASection } from '../components/CTASection';
import { useSEO } from '../lib/seo';
import { INSIGHTS } from '../data/content';

const EASE = [0.16, 1, 0.3, 1];

export default function InsightArticle() {
    const { id } = useParams();
    const article = INSIGHTS.find((a) => a.id === id);

    useSEO(
        article ? `${article.title} | Planmodo Insights` : 'Insights | Planmodo',
        article ? article.excerpt : 'Practical writing on business continuity, disaster recovery and operational resilience from Planmodo.'
    );

    if (!article) return <Navigate to="/insights" replace />;

    const next = INSIGHTS[(INSIGHTS.indexOf(article) + 1) % INSIGHTS.length];

    return (
        <>
            <article className="relative overflow-hidden pt-44 pb-20 sm:pb-28" data-testid="insight-article">
                <div className="absolute inset-0 grid-lines opacity-60" />
                <div className="pointer-events-none absolute -top-24 right-1/4 h-96 w-96 rounded-full bg-deepteal/10 blur-[140px]" />
                <div className="relative mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
                    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
                        <Link
                            to="/insights"
                            data-testid="insight-back-link"
                            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-slate-400 transition-colors hover:text-deepteal"
                        >
                            <ArrowLeft size={14} /> All insights
                        </Link>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
                        className="mt-8 flex items-center gap-3"
                    >
                        <span className="rounded-full border border-deepteal/30 bg-deepteal/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-deepteal">
                            {article.category}
                        </span>
                        <span className="font-mono text-[11px] text-slate-400">{article.readTime}</span>
                    </motion.div>

                    <h1 className="mt-6 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.12] text-slate-50 text-balance" data-testid="insight-article-title">
                        <span className="block overflow-hidden pb-1">
                            <motion.span
                                className="block"
                                initial={{ y: '110%' }}
                                animate={{ y: '0%' }}
                                transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
                            >
                                {article.title}
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.45, ease: EASE }}
                        className="mt-6 text-base sm:text-lg leading-relaxed text-slate-300"
                    >
                        {article.excerpt}
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
                        className="mt-10 space-y-6 border-t border-slate-700 pt-10"
                    >
                        {article.body.map((para, i) => (
                            <p key={i} className="text-base leading-relaxed text-slate-200">{para}</p>
                        ))}
                    </motion.div>

                    <Reveal delay={0.1}>
                        <div className="mt-14 border-t border-slate-700 pt-8">
                            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">Next insight</p>
                            <Link
                                to={`/insights/${next.id}`}
                                data-testid="insight-next-link"
                                className="group mt-3 flex items-center justify-between gap-6 rounded-2xl border border-slate-700 bg-white p-6 shadow-sm transition-all duration-300 hover:border-deepteal/40 hover:shadow-md"
                            >
                                <div>
                                    <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-deepteal">{next.category}</p>
                                    <p className="mt-2 font-display text-lg font-bold text-slate-50 text-balance">{next.title}</p>
                                </div>
                                <ArrowUpRight size={18} className="shrink-0 text-slate-400 transition-colors group-hover:text-deepteal" />
                            </Link>
                        </div>
                    </Reveal>
                </div>
            </article>
            <CTASection
                title="Reading about resilience is step one."
                text="A thirty-minute consultation with a practitioner turns general guidance into a plan for your organization."
            />
        </>
    );
}
